import React, { useRef, useState } from 'react';
import { slugify } from '../utils/helpers';
import axios from 'axios';
import Arrow from '-!svg-react-loader!../assets/images/icons/dropdown-arrow.svg';

const GravityForm = ({
  form,
  hideLabels = false,
  hideTitle = false,
  hideDescription = false,
}) => {
  const gForm = useRef(null);
  const [message, setMessage] = useState(null);

  /**
   * NOTE: Annoyingly, the returned field data doesn't indicate whether or not
   * the field label is hidden. Soooo... I guess we have to manually and
   * form-globally set that for now.
   */
  const Label = ({ field }) => (
    <label
      className={`GForm__field__label${hideLabels ? ' ScreenReader' : ''}`}
      htmlFor={`input_${form.databaseId}_${field.id}`}
    >
      {field.label}
      {field.isRequired ? <span>*</span> : ''}
    </label>
  )

  const Legend = ({ field }) => (
    <legend className={`GForm__field__label${hideLabels ? ' ScreenReader' : ''}`}>
      {field.label}
    </legend>
  )

  const FieldSelect = (field) => (<>
    <Label field={field} />
    <select
      className="GForm__field__select"
      name={`input_${field.id}`}
      id={`input_${form.databaseId}_${field.id}`}
      defaultValue={field.defaultValue || ''}
      required={field.isRequired}
    >
      {field.placeholder && (
        <option value="">{field.placeholder}</option>
      )}
      {field.choices.map((choice, index) => (
        <option value={choice.value} key={index}>
          {choice.text}
        </option>
      ))}
    </select>
    <Arrow />
  </>)

  const FieldText = (field) => {
    let fieldType = 'text';

    switch (field.type) {
      case 'PHONE':
        fieldType = 'tel';
        break;
      case 'WEBSITE':
        fieldType = 'url';
        break;
      default:
        fieldType = field.type?.toLowerCase() || 'text';
    }

    return (<>
      <input
        type={fieldType}
        name={`input_${field.id}`}
        id={`input_${form.databaseId}_${field.id}`}
        required={field.isRequired}
        placeholder={field.placeholder ? field.placeholder : 'floating'}
        defaultValue={field.defaultValue}
      />
      <Label field={field} />
    </>)
  }

  const FieldTextarea = (field) => (<>
    <textarea
      name={`input_${field.id}`}
      id={`input_${form.databaseId}_${field.id}`}
      required={field.isRequired}
      placeholder={field.placeholder ? field.placeholder : 'floating'}
    />
    <Label field={field} />

  </>)

  const FieldSet = (field) => (
    <fieldset className="GForm__fieldset">
      <Legend field={field} />
      <div className="GForm__fieldset__fields">
        {field.inputs.filter((field) => !field.isHidden).map((field) => (
          <div className="GForm__fieldset__field" key={field.id}>
            {FieldText(field)}
          </div>
        ))}
      </div>
    </fieldset>
  )

  const FieldCheckbox = (field) => {
    const fieldType = field.type.toLowerCase();

    return (
      <fieldset className="GForm__fieldset">
        <Legend field={field} />
        <div className="GForm__checkboxes">
          {field.choices.map((choice, index) => (
            <label
              key={index}
              className={fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}
              htmlFor={`input_${form.databaseId}_${field.id}_${index + 1}`}
            >
              <input
                type={fieldType}
                name={`input_${field.id}.${fieldType === 'checkbox' && index + 1}`}
                id={`input_${form.databaseId}_${field.id}_${index + 1}`}
                value={choice.value}
                defaultChecked={choice.isSelected}
              />
              <span className="Checkbox__label">{choice.text}</span>
            </label>
          ))}
        </div>
      </fieldset>
    )
  }

  const FieldConsent = (field) => {
    const agreement = "I agree to the privacy policy";

    return (
      <fieldset className="GForm__fieldset">
        <Legend field={field} />
        <div className="GForm__checkboxes">
          <label
            className="Checkbox"
            htmlFor={`input_${form.databaseId}_${field.id}_1`}
          >
            <input
              type="checkbox"
              name={`input_${field.id}.1`}
              id={`input_${form.databaseId}_${field.id}_1`}
              aria-invalid="false"
            />
            <span className="Checkbox__label">{agreement}</span>
          </label>
          <input type="hidden" name={`input_${field.id}.2`} value={agreement} />
          <input type="hidden" name={`input_${field.id}.3`} value="1" />
        </div>
      </fieldset>
    )
  }

  const FieldFile = (field) => {
    let fieldType = "file"

    return (<>
      <label className='GForm__field__label' htmlFor={`input_${field.id}`}>
        {form.databaseId === 5 ? "Upload a photo or receipt" : "Your CV"}
      </label>
      <input
        type={fieldType}
        name={`input_${field.id}`}
        id={`input_${form.databaseId}_${field.id}`}
        required={field.isRequired}
        placeholder={field.placeholder ? field.placeholder : 'floating'}
        defaultValue={field.defaultValue}
      />
    </>)
  }

  const FormField = ({ data }) => {
    let classBase = 'GForm__field';
    let formClasses = classBase;

    if (data.type) {
      formClasses = formClasses + ` ${classBase}--type-${slugify(data.type)}`;
    }

    if (data.size) {
      formClasses = formClasses + ` ${classBase}--size-${slugify(data.size)}`;
    }

    if (data.cssClass) {
      formClasses = formClasses + ' ' + slugify(data.cssClass);
    }

    let content = null;

    switch (data.type) {
      case 'TEXT':
      case 'EMAIL':
      case 'PHONE':
      case 'WEBSITE':
      case 'NUMBER':
        content = FieldText(data);
        break;
      case 'TEXTAREA':
        content = FieldTextarea(data);
        break;
      case 'CHECKBOX':
      case 'RADIO':
        content = FieldCheckbox(data)
        break;
      case 'SELECT':
        content = FieldSelect(data);
        break;
      case 'NAME':
      case 'ADDRESS':
        content = FieldSet(data);
        break;
      case 'HTML':
        content = (<div dangerouslySetInnerHTML={{__html: data.content}} />)
        break;
      case 'CONSENT':
        content = FieldConsent(data);
        break;
      case 'FILEUPLOAD':
          content = FieldFile(data);
          break;
      default:
        console.error(`WARNING: Gravity Form with ID ${form.databaseId} is trying to load a field of type ${data.type}, which hasn't been built and/or tested headless-side yet. The field was ommitted from the form.`);
        break;
    }

    if (!content) { return null }

    return (
      <div className={formClasses}>
        {content}
      </div>
    )
  }

  const submitAction = (e) => {
    e.preventDefault();

    const formData = new FormData(gForm.current);
    let formValues = {};
    for (var pair of formData.entries()) { formValues[pair[0]] = pair[1] }

    // First, we validate the submission
    axios({
      method: 'post',
      url: `${process.env.GATSBY_WP_URL}/wp-json/gf/v2/forms/${form.databaseId}/submissions/validation`,
      data: JSON.stringify(formValues),
      auth: {
        username: process.env.GATSBY_GRAVITY_FORMS_KEY,
        password: process.env.GATSBY_GRAVITY_FORMS_SECRET,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(response => {
      // If form submission is invalid
      if (!response.data.is_valid) {
        setMessage({
          'type': 'Error',
          'content': 'Form submission is invalid. Please double check all fields.',
        })
        return false;
      }

      // If form submission is spam
      if (response.data.is_valid && response.data.is_spam) {
        setMessage({
          'type': 'Error',
          'content': 'Your submission has been identified as spam. It was not submitted.',
        })
        return false;
      }

      // If form submission is valid, submit entry
      if (response.data.is_valid && !response.data.is_spam) {
        // The only required parameter is form_id
        const formInfo = {
          'form_id': form.databaseId,
          'date_created': new Date().toLocaleString(),
        };

        if(form.title === 'Contact Us' && typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            event: 'conversion',
            conversionId: '743066783',
            conversionLabel: 'JMKUCK-11Z0aEJ-ZqeIC'
          });
        }

        formInfo['source_url'] = window.location.href;

        const formData = new FormData(gForm.current);
        for (var pair of formData.entries()) {
          formInfo[pair[0].replace('input_', '')] = pair[1]
        }
        axios({
          method: 'post',
          url: `${process.env.GATSBY_WP_URL}/wp-json/gf/v2/entries`,
          data: JSON.stringify(formInfo),
          auth: {
            username: process.env.GATSBY_GRAVITY_FORMS_KEY,
            password: process.env.GATSBY_GRAVITY_FORMS_SECRET,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(response => {
          setMessage({
            'type': 'Message',
            'content': form.confirmations.filter(item => item.isDefault)[0].message,
          })

          // Send email notification
          if(response.data?.id) {
            axios({
              method: 'post',
              url: `${process.env.GATSBY_WP_URL}/wp-json/gf/v2/entries/${response.data.id}/notifications`,
              auth: {
                username: process.env.GATSBY_GRAVITY_FORMS_KEY,
                password: process.env.GATSBY_GRAVITY_FORMS_SECRET,
              },
              headers: {
                'Content-Type': 'application/json',
              },
            }).catch(error => {
              console.error('Error sending entry notifications');
              console.error(error);
            })
          }
        }).catch(error => {
          setMessage({
            'type': 'Error',
            'content': 'Sorry, something went wrong.',
          })
          console.error(error);
        })
      }
    }).catch(error => {
      console.error('Form validation error');
      console.error(error);
    })
  }

  return (
    <form
      className="GForm"
      onSubmit={submitAction}
      id={`gform_${form.databaseId}`}
      ref={gForm}
    >
      {(!hideTitle || !hideDescription) && (
        <div className="GForm__header">
          {!hideTitle && (
            <h3 className="GForm__title">{form.title}</h3>
          )}
          {!hideDescription && form.description && (
            <div className="GForm__description">{form.description}</div>
          )}
        </div>
      )}

      {message && (
        <div className={message.type}>{message.content}</div>
      )}

      <div className="GForm__fields">
        {form.formFields.nodes.map((field) => (
          <FormField data={field} key={field.id} />
        ))}
      </div>

      <div className="GForm__footer">
        <button type="submit" className="GForm__submit Button--outline">
          {form.submitButton.text}
        </button>
      </div>
    </form>
  )
}

export default GravityForm;
